import { Badge, Button, cn, Flex, Icon } from '@kognitos/lattice';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type TRainSleepStatus = 'sleeping' | 'completed';

export interface IRainSleepCardProps {
  status: TRainSleepStatus;
  /** Requested duration in seconds. */
  durationSeconds?: number;
  /** Actual slept duration in seconds (string, per the proto). */
  sleptForSeconds?: string;
  /** Optional reason from the LLM's sleep tool call. */
  reason?: string;
  interruptedByUser?: boolean;
  /**
   * ms since epoch when the sleep was requested. When provided alongside
   * `durationSeconds`, the card renders a live countdown of remaining time.
   */
  startedAt?: number;
  /**
   * Wakes the thread early (the "Wake up" button, shown only while
   * `status === 'sleeping'`). Omitted entirely when absent — history replay
   * builds sleep cards with no live thread to wake.
   */
  onWake?: () => Promise<void>;
  className?: string;
}

const formatSeconds = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds - minutes * 60);
  if (minutes < 60) return `${minutes}m ${remSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes - hours * 60;
  return `${hours}h ${remMinutes}m`;
};

const TICK_INTERVAL_MS = 1000;

const useTickEverySecond = (enabled: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative setInterval-based ticker for live countdown UI
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
};

export function RainSleepCard({
  status,
  durationSeconds,
  sleptForSeconds,
  reason,
  interruptedByUser,
  startedAt,
  onWake,
  className,
}: IRainSleepCardProps) {
  const { t } = useTranslation('rain');
  const isSleeping = status === 'sleeping';
  const showCountdown =
    isSleeping && startedAt !== undefined && durationSeconds !== undefined;
  const now = useTickEverySecond(showCountdown);

  // Disables the button the instant it's clicked; released once the wake
  // request settles (`onWake` handles its own errors and never throws — see
  // `wakeThread` in `useRainThreadMessages`). The card itself disappears once
  // the real `sleep_completed` event lands, flipping `status` to `completed`.
  const [isWaking, setIsWaking] = useState(false);
  const handleWake = useCallback(async () => {
    if (!onWake || isWaking) return;
    setIsWaking(true);
    try {
      await onWake();
    } finally {
      setIsWaking(false);
    }
  }, [isWaking, onWake]);

  const sleptDuration = sleptForSeconds
    ? formatSeconds(Number(sleptForSeconds))
    : undefined;

  let sleepingDurationLabel: string | undefined;
  if (isSleeping) {
    if (showCountdown) {
      const elapsedSec = Math.max(0, (now - (startedAt as number)) / 1000);
      const remainingSec = Math.max(
        0,
        (durationSeconds as number) - elapsedSec,
      );
      sleepingDurationLabel = t('rain.sleep.remaining', {
        duration: formatSeconds(remainingSec),
      });
    } else if (durationSeconds !== undefined) {
      sleepingDurationLabel = t('rain.sleep.duration', {
        duration: formatSeconds(durationSeconds),
      });
    }
  }

  const title =
    status === 'completed'
      ? sleptDuration
        ? t('rain.sleep.completed', { duration: sleptDuration })
        : t('rain.sleep.completedNoDuration')
      : t('rain.sleep.running');

  return (
    <Flex
      direction="column"
      gap={2}
      className={cn(
        'border border-border bg-muted/40 rounded-md p-3 w-full',
        className,
      )}
      data-testid="rain-sleep-card"
      data-status={status}
    >
      <Flex direction="row" align="center" gap={2}>
        <Icon
          type={isSleeping ? 'Moon' : 'Sparkles'}
          size="sm"
          className="text-muted-foreground shrink-0"
        />
        <span className="text-sm font-medium text-foreground">{title}</span>
        {interruptedByUser && status === 'completed' && (
          <Badge variant="secondary">{t('rain.sleep.interrupted')}</Badge>
        )}
      </Flex>

      {isSleeping && (sleepingDurationLabel || onWake) && (
        <Flex direction="row" align="center" justify="between" gap={2}>
          <span className="text-xs text-muted-foreground">
            {sleepingDurationLabel}
          </span>
          {onWake && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-xs text-muted-foreground"
              disabled={isWaking}
              onClick={() => void handleWake()}
              data-testid="rain-sleep-wake-button"
            >
              <Icon
                type={isWaking ? 'Loader2' : 'Sun'}
                size="sm"
                className={isWaking ? 'animate-spin' : undefined}
              />
              {t('rain.sleep.wakeUp')}
            </Button>
          )}
        </Flex>
      )}

      {reason && (
        <span className="text-xs text-muted-foreground">
          {t('rain.sleep.reason', { reason })}
        </span>
      )}
    </Flex>
  );
}
